import React, { useCallback, useEffect, useRef, useState } from "react";
import useDesktopChecker from "hooks/useDesktopChecker";
import EsignGif from "assets/images/v2/Esign-gif.gif";
import EsignReview from "assets/images/v2/EsignReview.gif";
import EsignCompleted from "assets/images/v2/EsignCompleted.gif";
import { ReactComponent as Pen } from "assets/images/v2/Pen.svg";
import { ReactComponent as Iconography }  from "assets/images/v2/Iconography.svg";
import chevronLeft from "assets/images/v2/chevron-left.svg";
import { ESignFSEventType, ESignStatus, useMarkESignFSEventSentMutation } from "../../dashboardService";
import { Button } from "components/v2/button/Button";
import Drawer, { DrawerType } from "components/ui/drawer/Drawer";
import ElectronicSignature from "pages/e-sign/ElectronicSignature";
import { 
  ESIGN_TITLE, 
  ESIGN_CLOSE_BUTTON_TEXT,
  ESIGN_FOOTER_TEXT,
  ESIGN_REDIRECTION_TOKEN,
  DISCLOSURE_TITLE_PREFIX,
  CLOSING_DISCLOSURE_TITLE_PREFIX
} from "utilities/Constant";
import "./ESignPanel.scss";
import useTabletChecker from "hooks/useTabletChecker";
import { useSelector } from "react-redux";
import { getCurrentUserName, getDealId, getEncompassLoanId, getLoanNumber, getNhDealApplicationId, getProspectId } from "features/authSlice";
import { trackFSESignStarted, sessionESignEventKeys } from "utilities/full-story";
import { getLoanProgress } from "pages/dashboard/store/dashboardSlice";

export interface ESignPanelProps {
  status?: string;
  needId?: string;
  loanAccessToken?: string;
  packageId?: string;
  title?: string;
  readonly setShowUpload?: (show: boolean) => void;
  isEnabled?: boolean;
  prospectId?: string;
  isEsignStartedFSEventSent?: boolean;
  onESignDrawerClosed?: () => void;
}

const ESignPanel: React.FC<ESignPanelProps> = ({
  status,
  needId,
  loanAccessToken,
  packageId,
  title,
  setShowUpload,
  isEnabled,
  prospectId,
  isEsignStartedFSEventSent,
  onESignDrawerClosed,
}) => {
  const isDesktop = useDesktopChecker();
  const isTablet = useTabletChecker();
  const [isESignDrawerOpen, setIsESignDrawerOpen] = useState(false);
  const username = useSelector(getCurrentUserName);
  const nhDealApplicationId = useSelector(getNhDealApplicationId);
  const selectorProspectId = useSelector(getProspectId);
  const dealId = useSelector(getDealId);
  const loanNumber = useSelector(getLoanNumber);
  const losGuid = useSelector(getEncompassLoanId);
  const loanProgress = useSelector(getLoanProgress);
  const [markESignFSEventSent] = useMarkESignFSEventSentMutation();
  /** Drawer opened via "E-Sign" (not "View"); parent refetch only applies to this path. */
  const openedViaESignButtonRef = useRef(false);
  /** Vendor script `onload` fired for this drawer session (interim gate until postMessage / completion). */
  const eccVendorScriptLoadedRef = useRef(false);
  const startedEventTrackedRef = useRef(false);

  const handleEccScriptLoaded = useCallback(() => {
    eccVendorScriptLoadedRef.current = true;
  }, []);

  const handleEccScriptError = useCallback(() => {
    eccVendorScriptLoadedRef.current = false;
  }, []);

  useEffect(() => {
    if (!isDesktop && !isTablet) {
      document.body.classList.add('esign-panel-visible');
    }
    return () => {
      document.body.classList.remove('esign-panel-visible');
    };
  }, [isDesktop, isTablet]);

  const trackStartedEvent = useCallback(() => {
    const normalizedTitle = title?.replace(/\s/g, "").toLowerCase();
    let titlePrefix: typeof DISCLOSURE_TITLE_PREFIX | typeof CLOSING_DISCLOSURE_TITLE_PREFIX | undefined;
    if (normalizedTitle?.startsWith(DISCLOSURE_TITLE_PREFIX)) {
      titlePrefix = DISCLOSURE_TITLE_PREFIX;
    } else if (normalizedTitle?.startsWith(CLOSING_DISCLOSURE_TITLE_PREFIX)) {
      titlePrefix = CLOSING_DISCLOSURE_TITLE_PREFIX;
    }

    const sessionKey = [
      nhDealApplicationId,
      prospectId ?? selectorProspectId,
      packageId,
      titlePrefix,
      ESignFSEventType.Started,
    ].join(":");

    if (
      startedEventTrackedRef.current ||
      isEsignStartedFSEventSent ||
      sessionESignEventKeys.has(sessionKey) ||
      !title ||
      !packageId ||
      !nhDealApplicationId
    ) {
      return;
    }

    startedEventTrackedRef.current = true;
    sessionESignEventKeys.add(sessionKey);
    trackFSESignStarted({
      username,
      prospectId: prospectId ?? selectorProspectId,
      dealId: dealId || null,
      timestamp: new Date().toISOString(),
      losLoanNumber: loanNumber || null,
      losGuid: losGuid || null,
      loanMilestone: loanProgress?.status ?? "Unknown",
    });

    if (titlePrefix) {
      void markESignFSEventSent({
        nhDealApplicationId,
        prospectId: prospectId ?? selectorProspectId,
        packageId: packageId,
        titlePrefix,
        eventType: ESignFSEventType.Started,
      }).unwrap().catch(() => {
        startedEventTrackedRef.current = false;
        sessionESignEventKeys.delete(sessionKey);
      });
    }
  }, [
    isEsignStartedFSEventSent,
    nhDealApplicationId,
    packageId,
    title,
    markESignFSEventSent
  ]);

  const handleESignClick = () => {
    if (loanAccessToken && packageId) {
      trackStartedEvent();
      openedViaESignButtonRef.current = true;
      eccVendorScriptLoadedRef.current = false;
      localStorage.setItem(ESIGN_REDIRECTION_TOKEN, packageId);
      setIsESignDrawerOpen(true);
    }
  };

  const handleESignDrawerClose = () => {
    setIsESignDrawerOpen(false);
    const shouldRefetchNeeds =
      openedViaESignButtonRef.current && eccVendorScriptLoadedRef.current;
    openedViaESignButtonRef.current = false;
    eccVendorScriptLoadedRef.current = false;
    if (shouldRefetchNeeds) {
      onESignDrawerClosed?.();
    }
  };

  const handleViewClick = () => {
    if (loanAccessToken && packageId) {
      openedViaESignButtonRef.current = false;
      eccVendorScriptLoadedRef.current = false;
      localStorage.setItem(ESIGN_REDIRECTION_TOKEN, packageId);
      setIsESignDrawerOpen(true);
    }
  };

  return (
    <div className="esign-panel">
      {(!isDesktop && !isTablet) && (
        <button
          className="esign-panel__back-button"
          onClick={() => setShowUpload?.(false)}
          type="button"
        >
          <img src={chevronLeft} alt="Left arrow" />
          <p>Back to tasks</p>
        </button>
      )}

      {status?.toLowerCase() === ESignStatus.NEW && (
        <section className="esign-panel__section esign-panel__section--new">
          <img src={EsignGif} alt="Please E-Sign" />
          <span className="paragraph-medium">
            Please click E-Sign to review and sign your disclosure
          </span>
          <Button 
            label="E-Sign" 
            variant="primary" 
            icon={<Pen />} 
            iconPosition="left"
            disabled={!isEnabled}
            onClick={handleESignClick}
          />
        </section>
      )}

      {status?.toLowerCase() === ESignStatus.COMPLETED && (
        <section className="esign-panel__section esign-panel__section--completed">
          <img src={EsignCompleted} alt="Completed" />
          <span className="paragraph-medium">Click view to see your Disclosures</span>  
          <Button 
            label="View" 
            variant="primary" 
            icon={<Iconography />} 
            iconPosition="left"
            onClick={handleViewClick}
            disabled={!isEnabled}
          />
        </section>
      )}

      {status?.toLowerCase() === ESignStatus.UNDER_REVIEW && (
        <section className="esign-panel__section">
          <img src={EsignReview} alt="Review" />
          <div className="esign-panel__title">Under Review</div>
          <span className="esign-panel__description">
            If you e-signed all your documents we will mark this task as completed shortly. If you didn't finish, click E-sign again to complete this task.
          </span>
          <Button 
            label="E-Sign" 
            variant="primary" 
            icon={<Pen />} 
            iconPosition="left"
            disabled={!isEnabled}
            onClick={handleESignClick}
          />
        </section>
      )}

      {isESignDrawerOpen && (
        <Drawer
          isOpen={isESignDrawerOpen}
          onClose={handleESignDrawerClose}
          title={ESIGN_TITLE}
          closeText={ESIGN_CLOSE_BUTTON_TEXT}
          footerText={ESIGN_FOOTER_TEXT}
          type={DrawerType.ElectronicSignature}
          centerContent
        >
          <ElectronicSignature
            loanAccessToken={loanAccessToken}
            packageId={packageId}
            onScriptLoad={handleEccScriptLoaded}
            onScriptError={handleEccScriptError}
          />
        </Drawer>
      )}
    </div>
  );
};

export default ESignPanel;